/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module LogsModule */

import {
    AfterViewInit,
    Component,
    Input,
    OnInit,
    TemplateRef,
    ViewChild,
} from '@angular/core';

import { customTemplates, templatesConfiguration } from 'ng/modules/logs/constants/logs-signpost.constants';

import { L10nService } from '@vmw/ngx-vip';
import { IAviDataGridConfig } from 'ng/modules/data-grid';
import * as globalL10n from 'global-l10n';
import { getWafGroupsRequestParams } from 'ng/modules/logs/utils/logs-signpost.utils';
import { addPoolIdFilterParams } from 'ng/modules/logs/utils/logs.utils';
import { TrafficLogsSignpostStore } from '../../../traffic-logs/traffic-logs-sidebar/traffic-logs-signpost.store';
import {
    FilterOperatorType,
    ILogsWafRuleGroupsRules,
    ILogsWafRuleGroupsSignpostData,
} from '../../../../logs.types';
import './logs-signpost-custom-waf-rule-groups.component.less';

const { ...globalL10nKeys } = globalL10n;

/**
 * @description Signpost component for WAF rule groups section.
 * @author Suraj Kumar, Yogesh Patil
 */
@Component({
    selector: 'logs-signpost-custom-waf-rule-groups',
    templateUrl: './logs-signpost-custom-waf-rule-groups.component.html',
})
export class LogsSignpostCustomWafRuleGroupsComponent implements OnInit, AfterViewInit {
    /**
     * The ID of the pool used to filter logs from a VS.
     */
    @Input()
    public poolId?: string;

    /**
     * TemplateRef for Expanded content of rule group name.
     */
    @ViewChild('expandedContentNameFieldTemplateRef')
    public readonly expandedContentNameFieldTemplateRef: TemplateRef<HTMLElement>;

    /**
     * TemplateRef for rule groups name.
     */
    @ViewChild('nameFieldTemplateRef')
    public readonly nameFieldTemplateRef: TemplateRef<HTMLElement>;

    public readonly FilterOperatorType = FilterOperatorType;

    public wafGroupsGridConfig: IAviDataGridConfig;

    public readonly globalL10nKeys = globalL10nKeys;

    constructor(
        public readonly trafficLogsSignpostStore: TrafficLogsSignpostStore,
        private readonly l10nService: L10nService,
    ) {
    }

    /** @override */
    public ngOnInit(): void {
        const fullSignpostParams = addPoolIdFilterParams(
            getWafGroupsRequestParams(), this.poolId,
        );

        this.trafficLogsSignpostStore.getLogSignpostData(fullSignpostParams);
    }

    /** @override */
    public ngAfterViewInit(): void {
        const { l10nService } = this;
        const configKey = customTemplates.wafRuleGroups;

        this.wafGroupsGridConfig = {
            layout: {
                hidePagination: true,
                hideCheckboxes: true,
                showFooter: false,
            },
            fields: [
                {
                    label: templatesConfiguration[configKey].name,
                    id: 'name',
                    templateRef: this.nameFieldTemplateRef,
                    expandedTemplateRef: this.expandedContentNameFieldTemplateRef,
                },
                {
                    label: l10nService.getMessage(globalL10nKeys.logsLabel),
                    id: 'logs',
                    transform: (
                        row: ILogsWafRuleGroupsSignpostData,
                    ): number => row.count,
                    expandedTransform: (
                        row: ILogsWafRuleGroupsRules,
                    ): number => row.count,
                },
            ],
            isExpandedContentColumnBased: true,
        };
    }

    public trackbyIndex(index: number): number {
        return index;
    }
}
